import React, { useState } from 'react';
import {
  AppLayout,
  ContentLayout,
  SideNavigation,
  BreadcrumbGroup,
  Tabs,
  Header,
  SpaceBetween,
  Table,
  Box,
  Link
} from '@cloudscape-design/components';
import { OverviewTab } from './tabs/OverviewTab';
import { CodeAnalysisTab } from './tabs/CodeAnalysisTab';
import { ClassClustersTab } from './tabs/ClassClustersTab';
import { DataRelationshipsTab } from './tabs/DataRelationshipsTab';
import { OperationFlowsTab } from './tabs/OperationFlowsTab';
import { CodeGraphTab } from './tabs/CodeGraphTab';

export function TechnicalAnalysisPage() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Navigation structure
  const navItems = [
    { type: 'link', text: 'Projects', href: '/jobs' },
    {
      type: 'section',
      text: 'Technical Analysis',
      items: [
        { type: 'link', text: 'Analysis Dashboard', href: '/technical-analysis' },
        {
          type: 'section',
          text: 'Recent Projects',
          items: [
            { type: 'link', text: 'ECommerce Platform', href: '/technical-analysis/ecommerce' },
            { type: 'link', text: 'Banking Portal', href: '/technical-analysis/banking' },
            // More projects...
          ]
        }
      ]
    }
  ];

  // Breadcrumb items based on selection
  const breadcrumbItems = [
    { text: 'Technical Analysis', href: '/technical-analysis' },
    ...(selectedProject ? [{ text: selectedProject, href: `/technical-analysis/${selectedProject}` }] : []),
    ...(activeTab !== 'overview' ? [{ text: activeTab }] : [])
  ];

  return (
    <AppLayout
      navigation={
        <SideNavigation
          activeHref={selectedProject ? `/technical-analysis/${selectedProject}` : '/technical-analysis'}
          header={{ text: 'Navigation' }}
          items={navItems}
        />
      }
      content={
        <ContentLayout
          header={
            <SpaceBetween size="m">
              <BreadcrumbGroup items={breadcrumbItems} />
              {selectedProject ? (
                <Header
                  variant="h1"
                  description="Technical analysis results and insights"
                >
                  {selectedProject}
                </Header>
              ) : (
                <ProjectSelectionView onSelect={setSelectedProject} />
              )}
            </SpaceBetween>
          }
        >
          {selectedProject ? (
            <Tabs
              tabs={[
                {
                  label: 'Overview',
                  id: 'overview',
                  content: <OverviewTab project={selectedProject} />
                },
                {
                  label: 'Code Analysis',
                  id: 'code-analysis',
                  content: <CodeAnalysisTab project={selectedProject} />
                },
                {
                  label: 'Class Clusters',
                  id: 'class-clusters',
                  content: <ClassClustersTab project={selectedProject}></ClassClustersTab>
                },
                {
                  label: 'Data Relationships',
                  id: 'data-relationships',
                  content: <DataRelationshipsTab project={selectedProject}></DataRelationshipsTab>
                },
                {
                  label: 'Operation Flows',
                  id: 'operation-flows',
                  content: <OperationFlowsTab project={selectedProject}></OperationFlowsTab>
                },
                {
                  label: 'Code Graph',
                  id: 'code-graph',
                  content: <CodeGraphTab project={selectedProject}></CodeGraphTab>
                }
              ]}
              activeTabId={activeTab}
              onChange={({ detail }) => setActiveTab(detail.activeTabId)}
            />
          ) : null}
        </ContentLayout>
      }
      toolsPanel={selectedProject && <AnalysisToolsPanel project={selectedProject} />}
      contentType="default"
      toolsOpen={false}
    />
  );
};

// Project Selection View when no project is selected
const ProjectSelectionView = ({ onSelect }) => {
  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Select a project to view technical analysis results"
      >
        Technical Analysis Dashboard
      </Header>
      <Table
        columnDefinitions={[
          {
            id: 'name',
            header: 'Project name',
            cell: item => item.name,
            sortingField: 'name'
          },
          {
            id: 'status',
            header: 'Analysis status',
            cell: item => item.status
          },
          {
            id: 'lastUpdated',
            header: 'Last updated',
            cell: item => item.lastUpdated
          }
        ]}
        items={[
          {
            name: 'ECommerce Platform',
            status: 'Analysis complete',
            lastUpdated: '2 hours ago'
          },
          // More projects...
        ]}
        onSelectionChange={({ detail }) => 
          onSelect(detail.selectedItems[0]?.name)
        }
        selectionType="single"
      />
    </SpaceBetween>
  );
};

// Tools panel component
const AnalysisToolsPanel = ({ project }) => {
  return (
    <SpaceBetween size="l">
      {/* Tools panel content */}
    </SpaceBetween>
  );
};
